import { useState, useEffect } from "react";
import { Sparkles, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { Transition } from "@headlessui/react";
import {
  getAllPrompts,
  deletePrompt,
  addNewPrompt,
} from "../../../../api/chat";
import { panelClasses } from "../utils/constants";

const DropDownPromptLoader = (props) => {
  const [promptList, setPromptList] = useState([]);
  const [canUserAddPrompts, setCanUserAddPrompts] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const refreshPromptList = () => {
    getAllPrompts().then((response) => {
      const sortedList = (response?.data || [])
        .map((item) => item.prompt)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setCanUserAddPrompts(sortedList.length < 10);
      setPromptList(sortedList);
    });
  };

  useEffect(() => {
    if (props.isPromptLoaderOpen) {
      refreshPromptList();
    }
  }, [props.isPromptLoaderOpen]);

  const addPromptToList = (prompt) => {
    addNewPrompt(prompt).then(() => refreshPromptList());
  };

  const deletePromptFromList = (prompt) => {
    deletePrompt(prompt).then(() => refreshPromptList());
  };

  const editPromptFromList = async (oldPrompt, newPrompt) => {
    await deletePrompt(oldPrompt);
    await addNewPrompt(newPrompt);
    refreshPromptList();
  };

  const smallBtnClasses =
    "inline-flex items-center justify-center rounded-md p-1.5 text-mutedText hover:text-mainText hover:bg-btnHover transition-colors cursor-pointer";

  const canAdd =
    canUserAddPrompts &&
    props.chatEntry?.trim() !== "" &&
    !promptList.includes(props.chatEntry?.trim());

  return (
    <div>
      <button
        onClick={() => {
          props.setIsPromptLoaderOpen(!props.isPromptLoaderOpen);
          props.setIsAgentSelectorOpen?.(false);
          props.setIsDropDownNotesOpen(false);
        }}
        className="inline-flex items-center gap-1.5 py-1 text-mutedText hover:text-mainText transition-colors font-light cursor-pointer"
      >
        <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
        <span className="text-[13px]">Prompts</span>
      </button>

      <Transition
        show={props.isPromptLoaderOpen}
        enter="transition duration-150 ease-out"
        enterFrom="opacity-0 translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-100 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="absolute left-0 right-0 bottom-full z-[70]">
          <div className={panelClasses}>
            <div className="max-h-[50vh] overflow-auto p-2">
              {!canUserAddPrompts && (
                <div className="mb-2 rounded-lg px-3 py-2 text-[12px] font-light bg-surface/80 text-mutedText">
                  You are using all 10 of your available prompts.
                </div>
              )}

              {canAdd && (
                <button
                  className="group mb-1 w-full flex items-center gap-2 rounded-lg border border-dashed border-inputBorder px-3 py-2 text-left text-[13px] font-light text-mutedText hover:border-velBlue/30 hover:text-mainText transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    addPromptToList(props.chatEntry.trim());
                  }}
                >
                  <Plus className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={2} />
                  <span className="truncate">{props.chatEntry.trim()}</span>
                </button>
              )}

              {promptList.length > 0 ? (
                <ul className="space-y-0.5">
                  {promptList.map((prompt) => (
                    <li key={prompt}>
                      {editingPrompt === prompt ? (
                        <div className="flex items-center gap-2 rounded-lg px-3 py-2 bg-surface/80">
                          <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            className="block w-full rounded-lg px-3 py-1.5 text-[13px] font-light bg-transparent border border-inputBorder text-mainText placeholder:text-mutedText focus:outline-none focus:ring-2 focus:ring-inputFocus/60"
                            placeholder="Edit prompt"
                            autoFocus
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (editingValue.trim()) {
                                editPromptFromList(prompt, editingValue.trim());
                              }
                              setEditingPrompt(null);
                            }}
                            className={smallBtnClasses}
                            aria-label="Save"
                          >
                            <Check
                              className="h-3.5 w-3.5 text-activeCase"
                              strokeWidth={2}
                            />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingPrompt(null);
                            }}
                            className={smallBtnClasses}
                            aria-label="Cancel"
                          >
                            <X
                              className="h-3 w-3 text-logoutColor"
                              strokeWidth={2}
                            />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            props.setChatEntry?.(prompt);
                            props.setIsPromptLoaderOpen(false);
                          }}
                          className="group flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-btnHover transition-colors cursor-pointer"
                        >
                          <p className="truncate pr-3 text-[13px] font-light text-mainText">
                            {prompt}
                          </p>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <span
                              className={smallBtnClasses}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPrompt(prompt);
                                setEditingValue(prompt);
                              }}
                              aria-label="Edit"
                            >
                              <Pencil className="h-3 w-3" strokeWidth={2} />
                            </span>
                            <span
                              className={smallBtnClasses}
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePromptFromList(prompt);
                              }}
                              aria-label="Delete"
                            >
                              <Trash2
                                className="h-3 w-3 text-logoutColor"
                                strokeWidth={2}
                              />
                            </span>
                          </div>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-3 py-6 text-[13px] font-light text-mutedText">
                  No saved prompts yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default DropDownPromptLoader;
